import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import AuthModal from "../components/AuthModal";

type AuthMode = "signin" | "signup";

type GuestAuthContextType = {
  /** True when the visitor has no valid session token. */
  isGuest: boolean;
  openSignIn: () => void;
  openSignUp: () => void;
};

const GuestAuthContext = createContext<GuestAuthContextType | undefined>(undefined);

type ProviderProps = {
  children: React.ReactNode;
  /** App-level session token (from the single useToken() in App). */
  token: string | null;
  setToken: (token: string) => void;
};

/**
 * Provides Sign In / Sign Up modal controls for guest browse pages so CTAs
 * like "Log in to contact seller" can open auth without leaving the page.
 * When a session exists, the modal is never shown and open* are no-ops.
 */
export function GuestAuthProvider({ children, token, setToken }: ProviderProps) {
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>("signin");

  const isGuest = !token;

  const openSignIn = useCallback(() => {
    if (token) return;
    setAuthMode("signin");
    setAuthOpen(true);
  }, [token]);

  const openSignUp = useCallback(() => {
    if (token) return;
    setAuthMode("signup");
    setAuthOpen(true);
  }, [token]);

  const value = useMemo(
    () => ({ isGuest, openSignIn, openSignUp }),
    [isGuest, openSignIn, openSignUp]
  );

  return (
    <GuestAuthContext.Provider value={value}>
      {children}
      {isGuest && (
        <AuthModal
          open={authOpen}
          onClose={() => setAuthOpen(false)}
          initialMode={authMode}
          setToken={setToken}
        />
      )}
    </GuestAuthContext.Provider>
  );
}

export function useGuestAuth(): GuestAuthContextType {
  const ctx = useContext(GuestAuthContext);
  // Safe fallback for isolated tests / pages outside the provider.
  return (
    ctx ?? {
      isGuest: true,
      openSignIn: () => {
        window.location.href = "/login";
      },
      openSignUp: () => {
        window.location.href = "/signup";
      },
    }
  );
}
